
import { supabase } from '@/lib/supabase';
import { AdminState } from '@/types/adminTypes';
import { SUPABASE_TABLE, DEFAULT_ID, SUPABASE_ENDPOINT } from './adminStateConfig';
import { mapDbRecordToState, mapUpdatesToDbRecord } from './adminStateMapper';

// Cache for pending operations
const pendingOperations = new Set<string>();

/**
 * Load admin state from Supabase with better error handling
 */
export const fetchStateFromDb = async (): Promise<AdminState | null> => {
  const operationId = `fetch-${Date.now()}`;
  pendingOperations.add(operationId);
  
  try {
    const { data, error } = await supabase
      .from(SUPABASE_TABLE)
      .select('*')
      .eq('id', DEFAULT_ID)
      .limit(1)
      .single();
    
    if (error) {
      throw new Error(`Error fetching admin state: ${error.message}`);
    }
    
    return mapDbRecordToState(data);
  } catch (error) {
    throw error;
  } finally {
    pendingOperations.delete(operationId);
  }
};

/**
 * Create default state in the database with retry logic
 */
export const createDefaultStateInDb = async (defaultState: AdminState): Promise<AdminState> => {
  const operationId = `create-${Date.now()}`;
  pendingOperations.add(operationId);
  
  try {
    const dbRecord = mapUpdatesToDbRecord(defaultState, defaultState);
    
    const { error } = await supabase
      .from(SUPABASE_TABLE)
      .upsert([dbRecord], { onConflict: 'id' });
    
    if (error) {
      throw new Error(`Error creating default state: ${error.message}`);
    }
    
    return defaultState;
  } catch (error) {
    // Return the default state even on error
    return defaultState;
  } finally {
    pendingOperations.delete(operationId);
  }
};

/**
 * Update admin state in the database with optimized upsert
 */
export const updateStateInDb = async (
  updates: Partial<AdminState>, 
  currentState: AdminState
): Promise<boolean> => {
  // Skip empty updates
  if (Object.keys(updates).length === 0) {
    return true;
  }
  
  const operationId = `update-${Date.now()}`;
  pendingOperations.add(operationId);
  
  try {
    const dbRecord = mapUpdatesToDbRecord(updates, currentState);
    
    const { error } = await supabase
      .from(SUPABASE_TABLE)
      .upsert([dbRecord], { 
        onConflict: 'id',
        ignoreDuplicates: false
      });
    
    if (error) {
      throw new Error(`Error updating admin state: ${error.message}`);
    }
    
    return true;
  } catch (error) {
    return false;
  } finally {
    pendingOperations.delete(operationId);
  }
};

/**
 * Set up browser unload handler to save pending state
 * with improved synchronization
 */
export const setupBeforeUnloadHandler = (
  getTimerRef: () => NodeJS.Timeout | null,
  clearTimerFunc: (timer: NodeJS.Timeout | null) => void,
  getPendingUpdates: () => Partial<AdminState>,
  clearPendingUpdates: () => void,
  getCurrentState: () => AdminState | null
): void => {
  if (typeof window === 'undefined') return;
  
  window.addEventListener('beforeunload', (event) => {
    const timer = getTimerRef();
    const pendingUpdates = getPendingUpdates();
    const stateCache = getCurrentState();
    
    if (timer) {
      clearTimerFunc(timer);
      
      // If we have pending updates, try to sync them before unload
      if (Object.keys(pendingUpdates).length > 0 && stateCache) {
        const supabaseData = mapUpdatesToDbRecord(pendingUpdates, stateCache);
        
        // Use sendBeacon for more reliable delivery during page unload
        try {
          // Instead of trying to access protected properties directly,
          // we use the publicly available API URL and create custom headers
          const apiUrl = `${SUPABASE_ENDPOINT}?on_conflict=id`;
          
          // Get the anonymous JWT token from localStorage if available
          const supabaseAnon = localStorage.getItem('supabase-auth-token');
          
          const headers = new Headers();
          headers.append('Content-Type', 'application/json');
          headers.append('apikey', process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '');
          
          // Use the token if available
          if (supabaseAnon) {
            try {
              const parsedToken = JSON.parse(supabaseAnon);
              if (parsedToken && parsedToken.length > 0 && parsedToken[0]) {
                headers.append('Authorization', `Bearer ${parsedToken[0]}`);
              }
            } catch (e) {
              // Ignore JSON parse errors
            }
          }
          
          navigator.sendBeacon(
            apiUrl,
            new Blob([JSON.stringify([supabaseData])], { type: 'application/json' })
          );
        } catch (error) {
          // Can't do anything with errors during unload
        }
        
        clearPendingUpdates();
      }
    }
  });
};
